import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn-ui/card';
import { Badge } from '@/components/shadcn-ui/badge';
import { Button } from '@/components/shadcn-ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/shadcn-ui/alert';
import { Separator } from '@/components/shadcn-ui/separator';
import {
  AlertCircle,
  TrendingUp,
  Clock,
  DollarSign,
  CheckCircle2,
  ArrowRight,
  Info,
  XCircle,
  Upload,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { appAxios } from '@/network/base/appAxios';
import ActivityLog from '@/components/ActivityLog';

const Dashboard = () => {
  const [activityLog, setActivityLog] = useState([]);
  const [permitRequests, setPermitRequests] = useState([]);

  const [globalStats, setGlobalStats] = useState({
    Last24HoursPermitCount: 0,
    MonthPermitCount: 0,
    BlockingPermitCount: 0,
  });
  const [loading, setLoading] = useState(true);

  const getGlobalStats = async () => {
    try {
      const res = await appAxios.get("/Admin/v1.0/global-stats");
      setGlobalStats(res.data.data);
    } catch (err) {
      console.error("Error fetching global stats", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getGlobalStats();
  }, []);

  const stats = [
    {
      id: 1,
      title: 'Total Requests (This Month)',
      value: globalStats.MonthPermitCount,
      change: 'Filled this month',
      icon: TrendingUp,
      iconBg: 'bg-blue-500/10',
      iconColor: 'text-blue-500',
    },
    {
      id: 2,
      title: 'Pending Action',
      value: globalStats.BlockingPermitCount,
      subtitle: 'Requires attention',
      icon: Clock,
      iconBg: 'bg-yellow-500/10',
      iconColor: 'text-yellow-500',
    },
    {
      id: 3,
      title: 'Awaiting Payment',
      value: '0',
      subtitle: 'Invoices sent',
      icon: DollarSign,
      iconBg: 'bg-green-500/10',
      iconColor: 'text-green-500',
    },
    {
      id: 4,
      title: 'Completed (This Week)',
      value: '0',
      subtitle: 'Permits delivered',
      icon: CheckCircle2,
      iconBg: 'bg-purple-500/10',
      iconColor: 'text-purple-500',
    },
  ];

  const quickFilters = [
    { label: 'My Assigned', count: 3, variant: 'default' },
    { label: 'Needs Manual', count: 2, variant: 'warning' },
    { label: 'Stuck >3 days', count: 1, variant: 'destructive' },
  ];


  const getPermitType = type => {
    const types = {
      '0': 'Bldg Alter Repair',
      '1': 'Electrical Vehicle Charger',
      '2': 'Bldg Alter Repair',
      '3': 'Fire Sprinkler',
      '4': 'HVAC',
      '5': 'Plumbing',
      '6': 'Pressure Vessel',
      '7': 'Temporary Special Event',
    };
    return types[type] || `${type}`;
  };

  const formatDate = (dateString, includeTime = true) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (includeTime) {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
      })
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })

    }
  };

  const fetchNewPermits = async () => {
    const response = await appAxios.get('/Admin/v1.0/new-permits');
    setPermitRequests(response.data.data);
  };

  const fetchRecentActivity = async () => {
    const response = await appAxios.get('/Admin/v1.0/new-activity-logs');
    setActivityLog(response.data.data);
  };


  useEffect(() => {
    fetchNewPermits();
    fetchRecentActivity();
  }, []);


  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">Overview of your permit pipeline</p>
      </div>

      {/* Alert */}
      <Alert variant="info" className="border-blue-500/50 bg-blue-500/10">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle className="flex items-center gap-2">
          {globalStats.Last24HoursPermitCount} new permits in the last 24 hours
        </AlertTitle>
        <AlertDescription className="flex items-center justify-between">
          <span>Review and assign them to your team</span>
          <Button variant="link" className="h-auto p-0 text-blue-600" asChild>
            <Link to="/admin/dashboard/requests">
              View Queue <ArrowRight className="ml-1 h-3 w-3" />
            </Link>
          </Button>
        </AlertDescription>
      </Alert>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                <div className={cn('rounded-lg p-2', stat.iconBg)}>
                  <Icon className={cn('h-4 w-4', stat.iconColor)} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground">{stat.change || stat.subtitle}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Filters */}
      {/* <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">Quick Filters:</CardTitle>
            <div className="flex flex-wrap gap-2">
              {quickFilters.map((filter, index) => (
                <Button key={index} variant="outline" size="sm" className="h-7 gap-2">
                  {filter.label}
                  <Badge
                    variant={
                      filter.variant === 'warning'
                        ? 'warning'
                        : filter.variant === 'destructive'
                          ? 'destructive'
                          : 'default'
                    }
                    className="h-5 px-1.5"
                  >
                    {filter.count}
                  </Badge>
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
      </Card> */}

      {/* Two Column Layout */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* New Permits */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle>New Permits</CardTitle>
            </div>
            <Button variant="link" className="h-auto p-0" asChild>
              <Link to="/admin/dashboard/requests">
                View all <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {permitRequests.map((permit, index) => (
                <div className='space-y-3' key={permit._id}>
                  <a href={`/admin/dashboard/permits/${permit._id}`}>
                    <div key={index} className='border p-3 rounded-md hover:bg-gray-100 cursor-pointer'>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex h-2 w-2 rounded-full bg-blue-500" />
                          <div>
                            <p className="text-sm font-medium">{permit._id.slice(-8)}</p>
                            <p className="text-xs text-muted-foreground">{getPermitType(permit.permit_type)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{formatDate(permit.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  </a>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle>Recent Activity</CardTitle>
            </div>
            {/* <Button variant="link" className="h-auto p-0" asChild>
              <Link to="/admin/dashboard/activity">
                View all <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button> */}
          </CardHeader>
          <CardContent>
            <ActivityLog activities={activityLog} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
